import type { Capability } from "@steelbill/types";
import { routes } from "@/routes/paths";

/**
 * WHERE A SIGNED-IN PERSON BELONGS (§11.2, §17.4, §2.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE HOME SCREEN IS A MAKING SCREEN, AND FOR SOMEBODY WHO MAKES NOTHING IT IS
 * A SCREEN OF THINGS THEY CANNOT DO.
 *
 * §11.2's home is four action tiles — New Invoice, New Quotation, Repeat Last
 * Bill, Customers — as "the LARGEST ELEMENT, above the fold on a phone", and
 * §1.1 argues at length that the buttons must not be pushed under a row of
 * statistics because "the owner opens this app to MAKE something".
 *
 * THREE OF THOSE FOUR NEED A CREATE CAPABILITY THE ACCOUNTANT DOES NOT HOLD.
 * `ActionTiles` filters correctly — nothing renders disabled and nothing 403s —
 * so what she actually gets is the hero collapsing to a single "Customers"
 * card above some figures. That is not a broken screen; it is worse. It is a
 * working screen that says nothing about her job, and it is the first thing an
 * external professional sees on the day she is given a login.
 *
 * HER JOB IS FILING AND RECONCILIATION, so she lands on REPORTS — §11.2's own
 * words for that screen are "This screen exists mainly to feed the CA", and
 * §17.4's register and GSTR-1 exports live on it. It is the reason she has a
 * login at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND IT IS DERIVED FROM CAPABILITIES, NEVER FROM `role === "ACCOUNTANT"`.
 *
 * §2.1 forbids the role branch in a component, and this is exactly the place it
 * would have been written: one `if` on her role, invisible for a year, and then
 * wrong the day a fourth role arrives holding some capabilities and not others.
 * What is asked here is a QUESTION ABOUT THE SCREEN — "can this person make
 * anything?" — which is the same question `ActionTiles` asks tile by tile, so
 * the answer cannot disagree with what the screen would have rendered.
 *
 * A ROLE THAT COULD CREATE WOULD LAND HOME, whatever it is called. OWNER and
 * ADMIN both hold `invoice.create`, so this resolves to `routes.home` for them
 * and nothing about their app changes.
 * ─────────────────────────────────────────────────────────────────────────────
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * The capabilities that make §11.2's home screen worth landing on.
 *
 * ANY ONE OF THEM IS ENOUGH — this is "can you start a document", not "can you
 * start every document". A future role that may only write quotations still
 * gets a hero with something in it.
 *
 * `customer.view` IS DELIBERATELY NOT HERE even though the fourth tile is gated
 * on it. The accountant holds it, and one tile called "Customers" is precisely
 * the near-empty screen this list exists to route her away from. A tile that
 * merely opens a register is not a reason to be on the making screen.
 */
export const HOME_SCREEN_CAPABILITIES: readonly Capability[] = Object.freeze([
  "invoice.create",
  "quotation.create",
  "challan.create",
]);

/** One candidate landing, and the capabilities that earn it. */
export interface LandingOption {
  path: string;
  /** Any one of these is enough. Never empty — an empty list would match nobody. */
  capabilities: readonly Capability[];
}

/**
 * The landings, in order of preference. The FIRST whose capabilities the caller
 * holds wins.
 *
 * ORDERED BY WHAT THE PERSON CAME TO DO, not by importance to the business:
 *
 *   HOME     — you make documents. §11.2's screen is yours.
 *   REPORTS  — you do not make documents but you file them. §17.4's exports.
 *   BILLS    — you can read the register and nothing else. Better than a screen
 *              of figures you cannot open.
 *
 * THE LAST RESORT IS `routes.home` ITSELF AND MUST STAY THAT WAY. `/` renders
 * the home screen rather than redirecting, so a caller matching nothing here
 * lands on a real screen; a fallback pointing anywhere else would bounce `/`
 * to a path whose own gate may fail, back to `/`, forever. Every path in the
 * list above is guarded by exactly the capability it is chosen for, so a match
 * here is a screen that will open.
 */
export const LANDING_ORDER: readonly LandingOption[] = Object.freeze([
  { path: routes.home, capabilities: HOME_SCREEN_CAPABILITIES },
  { path: routes.reports, capabilities: Object.freeze(["report.view"] as const) },
  { path: routes.invoices, capabilities: Object.freeze(["invoice.view"] as const) },
]);

/**
 * Where this person's app begins.
 *
 * `can` is the predicate from `useAuth()` — the same `can()` from
 * `packages/types` that `RequireCapability` and the server's `requireCap` call,
 * so the screen this returns is a screen that opens.
 */
export function landingPath(can: (capability: Capability) => boolean): string {
  const match = LANDING_ORDER.find((option) => option.capabilities.some((c) => can(c)));
  return match?.path ?? routes.home;
}

/**
 * Does §11.2's home screen belong to this person?
 *
 * Used by the sidebar to decide whether to offer a "Home" row at all. A nav
 * item that leads to a redirect is a lie about where it goes, and one that
 * lights up a DIFFERENT row when followed is worse — it teaches the reader
 * that the highlight cannot be trusted.
 */
export function landsOnHome(can: (capability: Capability) => boolean): boolean {
  return landingPath(can) === routes.home;
}
